import { Box, Grid, IconButton, Tooltip } from "@mui/material"
import TableGrid from "../table-grid/TableGrid"
import { GridColDef } from "@mui/x-data-grid"
import { memo, useMemo } from "react"
import { RootState, useAppDispatch, useAppSelector } from "../../store/store"
import { useTranslation } from "react-i18next"
import moment from "moment"
import { DeletePrediction, handleToggleDeletePredictionDialog, handleTogglePredictionDetailsDialog } from "../../store/prediction-slice/prediction"
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import { EndPoints } from "../../axios/endPoints"
import React from "react"
import DeleteDialog from "../common/DeleteDialog";
import PredictionDetailsDialog from "./PredictionDetailsDialog";
import { enqueueSnackbar } from "../../store/app-slice/app"
import { handleNotificationVariant } from "../notifications/_common"

interface PredictionsTableProps {
    predictionDataId: string | null
}

const PredictionsTable = ({ predictionDataId } : PredictionsTableProps) => {
    const dispatch = useAppDispatch();   
    const { t } = useTranslation();
    const { 
        refreshPredictionsTable,
        deletePredictionDialog,
        deletingPrediction,
        predictionDetailsDialog
    } = useAppSelector((state: RootState) => state.prediction);

    const columns: GridColDef[] = useMemo(() => [
        { field: 'id', minWidth: 80, headerName: t('PREDICTIONS_ID_FIELD') },
        { 
            field: 'name', 
            minWidth: 150,
            headerName: t('PREDICTIONS_NAME_FIELD')
        },
        { 
            field: 'processDate', 
            minWidth: 200,
            headerName: t('PREDICTIONS_DATE_FIELD'), 
            renderCell: (params) => { 
                return moment.utc(params.value).local().format('DD/MM/YYYY HH:mm')
            }, 
        },
        { 
            field: 'actions',
            sortable: false,
            minWidth: 150,
            headerName: t('PREDICTIONS_ACTIONS_FIELD'), 
            renderCell: (params) => {
                return(
                    <Box display="flex" gap={1} justifyContent="center">
                        <Tooltip title={t('PREDICTIONS_DETAILS_TOOLTIP')}>
                            <IconButton
                                onClick={() =>
                                    dispatch(
                                        handleTogglePredictionDetailsDialog({
                                            isOpen: true,
                                            body: params.row
                                        })
                                    )
                                }
                                size="small"
                            >
                                <InfoTwoToneIcon className="info-icon" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('PREDICTIONS_DELETE_TOOLTIP')}>
                            <IconButton
                                onClick={() =>
                                    dispatch(
                                        handleToggleDeletePredictionDialog({
                                            isOpen: true,
                                            body: params.row
                                        })
                                    )
                                }
                                size="small"
                            >
                                <DeleteForeverTwoToneIcon className="delete-icon" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        }
    ], [dispatch, t])

    const memoizedQueryParams = useMemo(
        () => ({ modelId: predictionDataId }),
        [predictionDataId]
    );

    return(
        <React.Fragment>
            <TableGrid 
                title={t('PREDICTIONS_TABLE')}
                baseURL={EndPoints.PREDICTION_GET_PREDICTIONS}
                aditionalQueryParams={memoizedQueryParams}
                columns={columns}
                sortColumns={[{ field: 'id', sort: 'asc' }]}
                disableRowSelection
                tableHeight={500}
                rowHeight={35}
                columnHeaderHeight={40}
                refresh={refreshPredictionsTable as number}
            />
            <DeleteDialog 
                open={deletePredictionDialog.isOpen}
                title={t('DELETE_PREDICTION_DIALOG_TITLE')} 
                bodyText={t('DELETE_PREDICTION_DIALOG_BODY', { name: deletePredictionDialog?.body?.name })} 
                isLoading={deletingPrediction} 
                onClose={function (reason?: string): void {
                    dispatch(handleToggleDeletePredictionDialog({ isOpen: false, body: null }));
                }} 
                onSubmit={function (): void {
                    dispatch(DeletePrediction(deletePredictionDialog?.body?.id)).then((res : any) => {
                        if(res && res.payload) { 
                            dispatch(enqueueSnackbar({ 
                                message: t(`${res.payload.message}`),
                                options: { 
                                    variant: handleNotificationVariant(res.payload.success),
                                    persist: !res.payload.success
                                } 
                            }))
                        }
                    });
                }} 
                cancelButtonText={t('DELETE_PREDICTION_DIALOG_CANCEL_BUTTON')} 
                submitButtonText={t('DELETE_PREDICTION_DIALOG_SUBMIT_BUTTON')}        
            />
            { predictionDetailsDialog && predictionDetailsDialog.isOpen ? <PredictionDetailsDialog /> : '' }
        </React.Fragment>
    )
}

export default memo(PredictionsTable);